import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../../store";
import {
  fetchManagerPendingRequests,
  updateManagerPendingRequest,
} from "../../../store/slices/managerWarehouseSlice";
import {
  fetchUserWarehouseStock,
  WarehouseStock,
} from "../../../store/slices/floorInwardSlice";
import Select from "react-select";
import { UNITS, UnitType } from "../../../models/warehouseRawMaterial.model";
import {
  getDefaultUnitForType,
  getUnitsByType,
} from "../../../utils/warehouse.util";
import PaginationControls from "../../../utils/PaginationControls";

const Button: React.FC<{
  type: string;
  onClick: () => void | Promise<void>;
  className: string;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ type, onClick, className, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-md font-medium transition-colors ${className}`}
    disabled={disabled}
  >
    {children}
  </button>
);

const PendingRequestsManager: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const { pendingLots, totalLots, totalLotPages, loading } = useSelector(
    (state: RootState) => state.managerWarehouse,
  );
  const { stock } = useSelector((state: RootState) => state.floorInward);

  const [lotPage, setLotPage] = useState(1);
  const [lotItemsPerPage] = useState(5);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [remarks, setRemarks] = useState<{ [id: string]: string }>({});
  const [suggestedQuantities, setSuggestedQuantities] = useState<{
    [id: string]: { quantity: number; unit: UnitType };
  }>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTrigger, setSearchTrigger] = useState("");
  const [selectedLotId, setSelectedLotId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchUserWarehouseStock());
    dispatch(
      fetchManagerPendingRequests({
        lotPage,
        lotLimit: lotItemsPerPage,
        search: searchTrigger,
      }),
    );
  }, [dispatch, lotPage, lotItemsPerPage, searchTrigger]);

  const handleSearch = () => {
    setSearchTrigger(searchQuery);
    setLotPage(1);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  };

  const updateRemark = (id: string, value: string) => {
    setRemarks((prev) => ({ ...prev, [id]: value }));
  };

  const updateSuggestedQuantity = (
    id: string,
    value: string,
    defaultUnit: UnitType,
  ) => {
    const num = parseFloat(value);
    setSuggestedQuantities((prev) => ({
      ...prev,
      [id]: {
        quantity: isNaN(num) ? 0 : num,
        unit: prev[id]?.unit ?? defaultUnit,
      },
    }));
  };

  const updateSuggestedUnit = (id: string, value: UnitType) => {
    setSuggestedQuantities((prev) => ({
      ...prev,
      [id]: { quantity: prev[id]?.quantity ?? 0, unit: value },
    }));
  };

  const handleAction = async (
    status: "approved" | "rejected" | "request change",
  ) => {
    if (!selectedIds.length) {
      console.warn("No requests selected for action.");
      return;
    }

    const requests = pendingLots.flatMap((lot) => lot.requests || []);
    if (!requests.length) {
      console.error("No requests found in pendingLots.");
      return;
    }

    const payload = selectedIds
      .map((id) => {
        const request = requests.find((req) => req._id === id);
        if (!request || !request._id) {
          console.warn(`Request with ID ${id} not found in pendingLots.`);
          return null;
        }
        const suggested = suggestedQuantities[id];
        return {
          id: request._id,
          status,
          remark: remarks[id] || "",
          ...(status === "request change"
            ? {
                suggestedQuantity: suggested?.quantity ?? request.quantity,
                suggestedUnit: suggested?.unit ?? request.unit,
              }
            : status === "approved"
              ? { fulfilledQuantity: suggested?.quantity ?? request.quantity }
              : {}),
        };
      })
      .filter((item) => item !== null) as {
      id: string;
      status: string;
      remark?: string;
      suggestedQuantity?: number;
      suggestedUnit?: UnitType;
      fulfilledQuantity?: number;
    }[];
    if (!payload.length) {
      console.error(
        "Payload is empty after filtering. No valid requests to process.",
      );
      return;
    }

    try {
      await dispatch(updateManagerPendingRequest(payload)).unwrap();
      setSelectedIds([]);
      setRemarks({});
      setSuggestedQuantities({});

      dispatch(
        fetchManagerPendingRequests({
          lotPage,
          lotLimit: lotItemsPerPage,
          search: searchTrigger,
        }),
      );
      dispatch(fetchUserWarehouseStock());
    } catch (error) {
      console.error("Failed to update requests:", error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "request change":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCurrentStock = (productId: string) => {
    const stockItem = stock.find(
      (item: WarehouseStock) => item._id === productId,
    );
    return stockItem
      ? `${
          typeof stockItem.quantity === "number"
            ? stockItem.quantity.toFixed(2)
            : stockItem.quantity
        } ${stockItem.unit}`
      : "-";
  };

  const handleLotPageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (totalLotPages || 1)) setLotPage(newPage);
  };

  if (!selectedLotId) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-gray-100 px-4 py-3 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
          <h2 className="text-base font-semibold text-gray-800">
            Pending Requests
          </h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              placeholder="Search by Order Id..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="w-full sm:w-72 px-2 py-1 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button
              type="button"
              onClick={handleSearch}
              className="w-full sm:w-auto bg-blue-100 text-blue-800 hover:bg-blue-200"
            >
              Search
            </Button>
          </div>
        </div>

        <div className="p-3">
          {pendingLots.length === 0 && (
            <p className="text-center text-xs text-slate-500">
              No pending Requests
            </p>
          )}
          {pendingLots.map((lot) => {
            const requester = lot.requests[0]?.userId;
            const username =
              typeof requester === "string"
                ? requester
                : requester?.username || "Unknown";
            const requestDate = lot.requests[0]?.createdAt
              ? new Date(lot.requests[0].createdAt).toLocaleDateString()
              : "-";
            return (
              <div
                key={lot.lotId}
                className="border p-3 mb-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setSelectedLotId(lot.lotId)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800 text-sm">
                      Order ID: {lot.lotId}
                    </p>
                    <p className="text-gray-600 text-xs">
                      Requested by: {username}
                    </p>
                    <p className="text-gray-600 text-xs">
                      Number of items: {lot.totalRequests}
                    </p>
                  </div>
                  <div className="text-gray-800 font-medium text-xs">
                    {requestDate}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="mt-4 border-t border-gray-200">
            <PaginationControls
              currentPage={lotPage}
              totalPages={totalLotPages || 1}
              onPageChange={handleLotPageChange}
              isLoading={loading}
              totalItems={totalLots}
            />
          </div>
        </div>
      </div>
    );
  }

  const lotRequests =
    pendingLots.find((lot) => lot.lotId === selectedLotId)?.requests || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl w-full max-w-[88vw] max-h-[88vh] overflow-hidden shadow-xl border border-slate-300">
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                Pending Requests (Lot {selectedLotId})
              </h2>
              <p className="text-[11px] text-slate-500 mt-1">
                Review and update selected requests quickly.
              </p>
            </div>
            <Button
              type="button"
              onClick={() => setSelectedLotId(null)}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 w-9 h-9 flex items-center justify-center rounded-full"
            >
              <span className="text-base font-semibold">×</span>
            </Button>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center mt-4">
            <Button
              type="button"
              onClick={() => handleAction("approved")}
              className="px-2 py-1 rounded-full bg-emerald-600 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:bg-emerald-300 disabled:text-slate-600"
              disabled={loading || !selectedIds.length}
            >
              Approve
            </Button>
            <Button
              type="button"
              onClick={() => handleAction("rejected")}
              className="px-2 py-1 rounded-full bg-rose-600 text-xs font-semibold text-white shadow-sm hover:bg-rose-700 disabled:bg-rose-300 disabled:text-slate-600"
              disabled={loading || !selectedIds.length}
            >
              Reject
            </Button>
            <Button
              type="button"
              onClick={() => handleAction("request change")}
              className="px-2 py-1 rounded-full bg-sky-600 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 disabled:bg-sky-300 disabled:text-slate-600"
              disabled={loading || !selectedIds.length}
            >
              Request Change
            </Button>
          </div>
        </div>

        <div className="max-h-[calc(88vh-118px)] overflow-y-auto p-2 sm:p-3">
          {loading && (
            <p className="text-center text-xs text-slate-500">Loading...</p>
          )}
          {!loading && lotRequests.length === 0 && (
            <p className="text-center text-xs text-slate-500">
              No pending requests
            </p>
          )}
          {!loading && lotRequests.length > 0 && (
            <div className="space-y-2">
              {lotRequests.map((req, index) => {
                if (!req._id) {
                  console.warn(`Missing _id for request ${index}:`, req);
                  return null;
                }
                const id = req._id;
                const quantityDisplay = `${req.quantity} ${UNITS.find((u) => u.value === req.unit)?.label || "Unkown Unit"}`;
                const username =
                  typeof req.userId === "string"
                    ? req.userId
                    : req.userId?.username || "Unknown";
                const requestDate = req.createdAt
                  ? new Date(req.createdAt).toLocaleDateString()
                  : "-";
                const historyRemarks =
                  req.history.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1 text-xs text-slate-700">
                      {req.history.map((hist, idx) => (
                        <li key={idx}>
                          <span className="font-semibold text-slate-800">
                            {hist.userId?.username || "Unknown"}:
                          </span>{" "}
                          {hist.remark || "-"}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-xs text-slate-500">-</span>
                  );
                return (
                  <div
                    key={id}
                    className={`rounded-2xl border p-3 shadow-sm transition hover:shadow-md ${
                      index % 2 === 0 ? "bg-white" : "bg-slate-50"
                    }`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-2">
                        <div className="mt-1">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(id)}
                            onChange={() => toggleSelect(id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-900">
                            {req.materialName ||
                              req.productId?.name ||
                              "Unknown"}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            Requested by {username}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 sm:text-right">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${getStatusColor(
                            req.status,
                          )}`}
                        >
                          {req.status.charAt(0).toUpperCase() +
                            req.status.slice(1)}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {requestDate}
                        </span>
                      </div>
                    </div>

                    <div className="grid gap-2 mt-3 sm:grid-cols-2">
                      <div className="rounded-2xl bg-slate-50 p-2">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">
                          Quantity
                        </p>
                        <p className="mt-2 text-xs font-semibold text-slate-900">
                          {quantityDisplay}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-2">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">
                          Current Stock
                        </p>
                        <p className="mt-2 text-xs font-semibold text-slate-900">
                          {getCurrentStock(req.productId?._id || "")}
                        </p>
                      </div>
                      <div className="sm:col-span-2 rounded-2xl bg-slate-50 p-2">
                        <p className="text-xs uppercase tracking-wide text-slate-500">
                          History Remarks
                        </p>
                        <div className="mt-2 text-xs text-slate-700">
                          {historyRemarks}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2 mt-3 lg:grid-cols-3">
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-slate-500">
                          Manager Remark
                        </label>
                        <input
                          type="text"
                          value={remarks[id] || ""}
                          onChange={(e) => updateRemark(id, e.target.value)}
                          className="w-full rounded-2xl border border-gray-300 bg-white px-2 py-1 text-xs shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          placeholder="Add remark..."
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-slate-500">
                          Suggested Quantity
                        </label>
                        <input
                          type="number"
                          value={suggestedQuantities[id]?.quantity || ""}
                          onChange={(e) =>
                            updateSuggestedQuantity(
                              id,
                              e.target.value,
                              req.unit,
                            )
                          }
                          className="w-full rounded-2xl border border-gray-300 bg-white px-2 py-1 text-xs shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          placeholder="Suggested quantity..."
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-slate-500">
                          Suggested Unit
                        </label>
                        <Select
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary: "#0077C0",
                              primary25: "#E0F2FE",
                            },
                          })}
                          options={
                            req.productId?.unitType
                              ? getUnitsByType(req.productId.unitType)
                              : []
                          }
                          value={
                            suggestedQuantities[id]?.unit &&
                            getUnitsByType(
                              req.productId?.unitType || "piece",
                            ).some(
                              (u) => u.value === suggestedQuantities[id].unit,
                            )
                              ? {
                                  value: suggestedQuantities[id].unit,
                                  label:
                                    getUnitsByType(
                                      req.productId?.unitType || "piece",
                                    ).find(
                                      (u) =>
                                        u.value ===
                                        suggestedQuantities[id].unit,
                                    )?.label || suggestedQuantities[id].unit,
                                }
                              : {
                                  value: getDefaultUnitForType(
                                    req.productId?.unitType || "piece",
                                  ),
                                  label:
                                    getUnitsByType(
                                      req.productId?.unitType || "piece",
                                    ).find(
                                      (u) =>
                                        u.value ===
                                        getDefaultUnitForType(
                                          req.productId?.unitType || "piece",
                                        ),
                                    )?.label ||
                                    getDefaultUnitForType(
                                      req.productId?.unitType || "piece",
                                    ),
                                }
                          }
                          onChange={(selectedOption) =>
                            updateSuggestedUnit(
                              id,
                              (selectedOption?.value as UnitType) ||
                                getDefaultUnitForType(
                                  req.productId?.unitType || "piece",
                                ),
                            )
                          }
                          placeholder="Select unit..."
                          isSearchable
                          isClearable
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: "#d1d5db",
                              boxShadow: "none",
                              borderRadius: "0.75rem",
                              minHeight: "30px",
                              height: "30px",
                              fontSize: "0.75rem",
                              padding: "0 6px",
                              "&:hover": {
                                borderColor: "#3b82f6",
                              },
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "30px",
                              minHeight: "30px",
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: "#6b7280",
                              fontSize: "0.75rem",
                              marginLeft: 0,
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                              fontSize: "0.75rem",
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: "#1f2937",
                              fontSize: "0.75rem",
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              padding: "0 4px",
                              height: "30px",
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: "0",
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: "0",
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 10000,
                              fontSize: "0.75rem",
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default PendingRequestsManager;
